import * as fabric from 'fabric';
export const handleGuideLines = (canvas, obj) => {
    const zoom = canvas.getZoom();
    const canvasWidth = canvas.width / zoom;
    const canvasHeight = canvas.height / zoom;
    const canvasSize = Math.min(canvasWidth, canvasHeight);
    const baseSnappingDistance = Math.min(5, Math.max(2, Math.log10(canvasSize) * 2));
    const standardSnappingDistance = Math.max(baseSnappingDistance, 2) / zoom;
    const centerSnappingDistance = standardSnappingDistance * 1.4;
    obj.setCoords();
    // Get actual object coordinates considering rotation
    const objCoords = getObjectCoordinates(obj);
    const objBoundingBox = {
        left: objCoords.tl.x,
        top: objCoords.tl.y,
        right: objCoords.br.x,
        bottom: objCoords.br.y,
        width: objCoords.br.x - objCoords.tl.x,
        height: objCoords.br.y - objCoords.tl.y,
        centerX: (objCoords.tl.x + objCoords.br.x) / 2,
        centerY: (objCoords.tl.y + objCoords.br.y) / 2
    };
    clearGuidLines(canvas);
    // Store all potential snap points to find the best ones
    const snapPoints = [];
    // Canvas edge snap points
    // Left edge
    if (Math.abs(objBoundingBox.left) < standardSnappingDistance) {
        snapPoints.push({
            type: 'horizontal',
            axis: 'x',
            target: 0,
            currentPosition: objBoundingBox.left,
            priority: 2, // Medium priority for canvas edges
            lineId: 'vertical-left',
            delta: -objBoundingBox.left
        });
    }
    // Top edge
    if (Math.abs(objBoundingBox.top) < standardSnappingDistance) {
        snapPoints.push({
            type: 'vertical',
            axis: 'y',
            target: 0,
            currentPosition: objBoundingBox.top,
            priority: 2,
            lineId: 'horizontal-top',
            delta: -objBoundingBox.top
        });
    }
    // Right edge
    if (Math.abs(objBoundingBox.right - canvasWidth) < standardSnappingDistance) {
        snapPoints.push({
            type: 'horizontal',
            axis: 'x',
            target: canvasWidth,
            currentPosition: objBoundingBox.right,
            priority: 2,
            lineId: 'vertical-right',
            delta: canvasWidth - objBoundingBox.right
        });
    }
    // Bottom edge
    if (Math.abs(objBoundingBox.bottom - canvasHeight) < standardSnappingDistance) {
        snapPoints.push({
            type: 'vertical',
            axis: 'y',
            target: canvasHeight,
            currentPosition: objBoundingBox.bottom,
            priority: 2,
            lineId: 'horizontal-bottom',
            delta: canvasHeight - objBoundingBox.bottom
        });
    }
    // Center horizontal to canvas center
    if (Math.abs(objBoundingBox.centerX - canvasWidth / 2) < centerSnappingDistance) {
        snapPoints.push({
            type: 'horizontal',
            axis: 'x',
            target: canvasWidth / 2,
            currentPosition: objBoundingBox.centerX,
            priority: 3, // Higher priority for center alignment
            lineId: 'vertical-center',
            delta: (canvasWidth / 2) - objBoundingBox.centerX
        });
    }
    // Center vertical to canvas center
    if (Math.abs(objBoundingBox.centerY - canvasHeight / 2) < centerSnappingDistance) {
        snapPoints.push({
            type: 'vertical',
            axis: 'y',
            target: canvasHeight / 2,
            currentPosition: objBoundingBox.centerY,
            priority: 3, // Higher priority for center alignment
            lineId: 'horizontal-center',
            delta: (canvasHeight / 2) - objBoundingBox.centerY
        });
    }
    // Snap to other objects
    const otherObjects = canvas.getObjects().filter((o) => o !== obj && o.selectable);
    otherObjects.forEach((otherObj) => {
        otherObj.setCoords();
        // Get coordinates of the other object considering rotation
        const otherCoords = getObjectCoordinates(otherObj);
        const otherBoundingBox = {
            left: otherCoords.tl.x,
            top: otherCoords.tl.y,
            right: otherCoords.br.x,
            bottom: otherCoords.br.y,
            width: otherCoords.br.x - otherCoords.tl.x,
            height: otherCoords.br.y - otherCoords.tl.y,
            centerX: (otherCoords.tl.x + otherCoords.br.x) / 2,
            centerY: (otherCoords.tl.y + otherCoords.br.y) / 2
        };
        // Center X alignment - HIGHEST PRIORITY
        if (Math.abs(objBoundingBox.centerX - otherBoundingBox.centerX) < centerSnappingDistance) {
            snapPoints.push({
                type: 'horizontal',
                axis: 'x',
                target: otherBoundingBox.centerX,
                currentPosition: objBoundingBox.centerX,
                priority: 4, // Highest priority for center-to-center alignment
                lineId: `vertical-obj-${otherObj.id}-center`,
                delta: otherBoundingBox.centerX - objBoundingBox.centerX
            });
        }
        // Center Y alignment - HIGHEST PRIORITY
        if (Math.abs(objBoundingBox.centerY - otherBoundingBox.centerY) < centerSnappingDistance) {
            snapPoints.push({
                type: 'vertical',
                axis: 'y',
                target: otherBoundingBox.centerY,
                currentPosition: objBoundingBox.centerY,
                priority: 4, // Highest priority for center-to-center alignment
                lineId: `horizontal-obj-${otherObj.id}-center`,
                delta: otherBoundingBox.centerY - objBoundingBox.centerY
            });
        }
        // Horizontal alignments (affecting X axis) - MEDIUM PRIORITY
        // Only check edge alignments if objects have similar heights or widths
        const similarWidth = Math.abs(objBoundingBox.width - otherBoundingBox.width) < objBoundingBox.width * 0.1;
        const similarHeight = Math.abs(objBoundingBox.height - otherBoundingBox.height) < objBoundingBox.height * 0.1;
        if (similarWidth) {
            // Left edge to left edge
            if (Math.abs(objBoundingBox.left - otherBoundingBox.left) < standardSnappingDistance) {
                snapPoints.push({
                    type: 'horizontal',
                    axis: 'x',
                    target: otherBoundingBox.left,
                    currentPosition: objBoundingBox.left,
                    priority: 1,
                    lineId: `vertical-obj-${otherObj.id}-left`,
                    delta: otherBoundingBox.left - objBoundingBox.left
                });
            }
            // Right edge to right edge
            if (Math.abs(objBoundingBox.right - otherBoundingBox.right) < standardSnappingDistance) {
                snapPoints.push({
                    type: 'horizontal',
                    axis: 'x',
                    target: otherBoundingBox.right,
                    currentPosition: objBoundingBox.right,
                    priority: 1,
                    lineId: `vertical-obj-${otherObj.id}-right`,
                    delta: otherBoundingBox.right - objBoundingBox.right
                });
            }
        }
        // Spacing snap points - LOWER PRIORITY
        // Right edge to left edge (objects next to each other)
        if (Math.abs(objBoundingBox.right - otherBoundingBox.left) < standardSnappingDistance) {
            snapPoints.push({
                type: 'horizontal',
                axis: 'x',
                target: otherBoundingBox.left,
                currentPosition: objBoundingBox.right,
                priority: 1,
                lineId: `vertical-obj-${otherObj.id}-left`,
                delta: otherBoundingBox.left - objBoundingBox.right
            });
        }
        // Left edge to right edge
        if (Math.abs(objBoundingBox.left - otherBoundingBox.right) < standardSnappingDistance) {
            snapPoints.push({
                type: 'horizontal',
                axis: 'x',
                target: otherBoundingBox.right,
                currentPosition: objBoundingBox.left,
                priority: 1,
                lineId: `vertical-obj-${otherObj.id}-right`,
                delta: otherBoundingBox.right - objBoundingBox.left
            });
        }
        // Vertical alignments (affecting Y axis) - MEDIUM PRIORITY
        if (similarHeight) {
            // Top edge to top edge
            if (Math.abs(objBoundingBox.top - otherBoundingBox.top) < standardSnappingDistance) {
                snapPoints.push({
                    type: 'vertical',
                    axis: 'y',
                    target: otherBoundingBox.top,
                    currentPosition: objBoundingBox.top,
                    priority: 1,
                    lineId: `horizontal-obj-${otherObj.id}-top`,
                    delta: otherBoundingBox.top - objBoundingBox.top
                });
            }
            // Bottom edge to bottom edge
            if (Math.abs(objBoundingBox.bottom - otherBoundingBox.bottom) < standardSnappingDistance) {
                snapPoints.push({
                    type: 'vertical',
                    axis: 'y',
                    target: otherBoundingBox.bottom,
                    currentPosition: objBoundingBox.bottom,
                    priority: 1,
                    lineId: `horizontal-obj-${otherObj.id}-bottom`,
                    delta: otherBoundingBox.bottom - objBoundingBox.bottom
                });
            }
        }
        // Spacing snap points - LOWER PRIORITY
        // Bottom edge to top edge (objects above/below each other)
        if (Math.abs(objBoundingBox.bottom - otherBoundingBox.top) < standardSnappingDistance) {
            snapPoints.push({
                type: 'vertical',
                axis: 'y',
                target: otherBoundingBox.top,
                currentPosition: objBoundingBox.bottom,
                priority: 1,
                lineId: `horizontal-obj-${otherObj.id}-top`,
                delta: otherBoundingBox.top - objBoundingBox.bottom
            });
        }
        // Top edge to bottom edge
        if (Math.abs(objBoundingBox.top - otherBoundingBox.bottom) < standardSnappingDistance) {
            snapPoints.push({
                type: 'vertical',
                axis: 'y',
                target: otherBoundingBox.bottom,
                currentPosition: objBoundingBox.top,
                priority: 1,
                lineId: `horizontal-obj-${otherObj.id}-bottom`,
                delta: otherBoundingBox.bottom - objBoundingBox.top
            });
        }
    });
    // If no snap points, just exit
    if (snapPoints.length === 0) {
        return;
    }
    // Group snap points by axis
    const xSnapPoints = snapPoints.filter(p => p.axis === 'x');
    const ySnapPoints = snapPoints.filter(p => p.axis === 'y');
    // Sort by priority (higher first) and then by distance (smallest first)
    const sortPoints = (points) => {
        return points.sort((a, b) => {
            if (a.priority !== b.priority) {
                return b.priority - a.priority; // Higher priority first
            }
            return Math.abs(a.currentPosition - a.target) - Math.abs(b.currentPosition - b.target);
        });
    };
    const sortedXPoints = sortPoints(xSnapPoints);
    const sortedYPoints = sortPoints(ySnapPoints);
    let bestXSnap = sortedXPoints.length > 0 ? sortedXPoints[0] : null;
    let bestYSnap = sortedYPoints.length > 0 ? sortedYPoints[0] : null;
    // Apply the best snaps and draw guidelines
    const guidesToDraw = [];
    if (bestXSnap) {
        // Apply X movement
        obj.left += bestXSnap.delta;
        // Add guideline
        const line = createVerticalGuidline(canvas, bestXSnap.target, bestXSnap.lineId);
        guidesToDraw.push(line);
    }
    if (bestYSnap) {
        // Apply Y movement
        obj.top += bestYSnap.delta;
        // Add guideline
        const line = createHorizontalGuidline(canvas, bestYSnap.target, bestYSnap.lineId);
        guidesToDraw.push(line);
    }
    // Update object coordinates after moving
    obj.setCoords();
    // Add all guidelines to canvas
    guidesToDraw.forEach(line => canvas.add(line));
    // Render the canvas
    canvas.renderAll();
};
/**
 * Gets the actual corner coordinates of an object, considering rotation
 */
function getObjectCoordinates(obj) {
    // These are the actual corner coordinates accounting for rotation
    const coords = {
        tl: obj.aCoords.tl,
        tr: obj.aCoords.tr,
        bl: obj.aCoords.bl,
        br: obj.aCoords.br
    };
    // Calculate bounding box based on these coordinates
    const minX = Math.min(coords.tl.x, coords.tr.x, coords.bl.x, coords.br.x);
    const maxX = Math.max(coords.tl.x, coords.tr.x, coords.bl.x, coords.br.x);
    const minY = Math.min(coords.tl.y, coords.tr.y, coords.bl.y, coords.br.y);
    const maxY = Math.max(coords.tl.y, coords.tr.y, coords.bl.y, coords.br.y);
    return {
        tl: { x: minX, y: minY },
        tr: { x: maxX, y: minY },
        bl: { x: minX, y: maxY },
        br: { x: maxX, y: maxY }
    };
}
/**
 * Moves an object by the specified delta, properly handling rotation
 */
function moveObjectWithRotation(obj, deltaX, deltaY) {
    // Simply move the object by the delta
    obj.left += deltaX;
    obj.top += deltaY;
    obj.setCoords(); // Update coordinates after moving
}
export const createVerticalGuidline = (canvas, x, id) => {
    const zoom = canvas.getZoom();
    return new fabric.Line([x, 0, x, canvas.height / zoom], {
        id,
        stroke: '#000000',
        strokeWidth: 1 / zoom,
        selectable: false,
        evented: false,
        strokeDashArray: [5, 5],
        opacity: 0.8,
    });
};
export const createHorizontalGuidline = (canvas, y, id) => {
    const zoom = canvas.getZoom();
    return new fabric.Line([0, y, canvas.width / zoom, y], {
        id,
        stroke: '#000000',
        strokeWidth: 1 / zoom,
        selectable: false,
        evented: false,
        strokeDashArray: [5, 5],
        opacity: 0.8,
    });
};
export const clearGuidLines = (canvas) => {
    const objects = canvas.getObjects('line');
    objects.forEach((obj) => {
        if (obj.id &&
            (obj.id.startsWith('vertical-') ||
                obj.id.startsWith('horizontal-') ||
                obj.id.startsWith('vertical-obj-') ||
                obj.id.startsWith('horizontal-obj-'))) {
            canvas.remove(obj);
        }
    });
    canvas.renderAll();
};
const guideLineExists = (canvas, id) => {
    const objects = canvas.getObjects('line');
    return objects.some((obj) => obj.id === id);
};
export const handleSelectedMenuVisibility = (hide) => {
    const menu = document.querySelector('#selected-object-menu');
    if (!menu)
        return;
    if (hide) {
        menu.classList.add('hidden');
    }
    else {
        menu.classList.remove('hidden');
    }
};
